import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  Box,
  Circle,
  Flex,
  Heading,
  IconButton,
  Stack,
  Text,
} from '@chakra-ui/react'
import timelineSeparatorBottom from 'assets/images/timeline-separator-bottom.svg'
import timelineSeparatorRight from 'assets/images/timeline-separator-right.svg'
import { useBreakpointContext } from 'context/Breakpoint'
import React, { useEffect, useState } from 'react'

export interface TimelineItem {
  title: string
  description: string
}

const timelineColors = ['#89a1e8', '#bcb1f0', '#d7ed74', '#5189f0', '#7d8aba']

const StaticTimeline: React.FC<{ timelineItems: TimelineItem[] }> = ({
  timelineItems,
}) => {
  const [isTimelineVisible, setIsTimelineVisible] = useState(true)
  const { isMobile } = useBreakpointContext()

  useEffect(() => {
    if (isMobile) {
      setIsTimelineVisible(false)
    } else {
      setIsTimelineVisible(true)
    }
  }, [isMobile])

  const toggleTimelineVisibility = () =>
    setIsTimelineVisible(!isTimelineVisible)

  return (
    <Box
      p={4}
      bg={'#f6f5ef'}
      mb={4}
      mr={4}
      ml={4}
      width="100%"
      minHeight={isMobile ? '60px' : 'auto'}
      borderRadius={'md'}
      gap={4}
      display="flex"
      flexDirection="column"
      rowGap={4}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Heading
          size="lg"
          fontFamily={'Inter'}
          fontWeight={'bold'}
          fontSize={'14px'}
          letterSpacing={'0.1em'}
          m={0}
        >
          GETTING YOU STARTED
        </Heading>

        <IconButton
          aria-label={isTimelineVisible ? 'Hide timeline' : 'Show timeline'}
          icon={isTimelineVisible ? <ChevronUpIcon /> : <ChevronDownIcon />}
          fontSize="30px"
          onClick={toggleTimelineVisibility}
          variant="ghost"
          _focus={{ boxShadow: 'none' }}
          _hover={{ bg: 'transparent' }}
          _active={{ bg: 'transparent' }}
        />
      </Flex>

      {isTimelineVisible && (
        <Flex
          justify="space-between"
          gap={isMobile ? 0 : 4}
          direction={isMobile ? 'column' : 'row'}
          flexWrap="nowrap"
        >
          {timelineItems.map((item, index) => (
            <Flex
              key={item.title}
              direction={isMobile ? 'row' : 'column'}
              flex="1"
            >
              <Flex direction="column" gap={0} position={'relative'}>
                <Circle
                  size="70px"
                  bg={`radial-gradient(circle, ${timelineColors[index]} 45%, #f6f5ef 70%);`}
                  color="black"
                >
                  <Circle
                    size="50px"
                    bg="transparent"
                    border="1px dashed #141743"
                    color="black"
                    fontWeight="semibold"
                    position="relative"
                    fontFamily={'Instrument Serif'}
                  >
                    {index + 1}
                  </Circle>
                </Circle>
                {isMobile
                  ? mobileTimelineItem(timelineItems, index)
                  : desktopTimelineItem(timelineItems, index)}
              </Flex>

              <Stack spacing={2} mt={isMobile ? 4 : '9px'} px={2}>
                <Text fontSize="xs" fontWeight="bold" textAlign="left" m={0}>
                  {item.title}
                </Text>
                <Text
                  fontSize="xs"
                  textAlign="left"
                  mr={isMobile || timelineItems.length >= 5 ? 0 : 12}
                >
                  {item.description}
                </Text>
              </Stack>
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  )
}

const mobileTimelineItem = (timelineItems: TimelineItem[], index: number) => {
  return (
    <>
      {index < timelineItems.length - 1 && (
        <Stack zIndex={1} position={'relative'} top={'-13px'} h={'40px'}>
          <img
            src={timelineSeparatorBottom}
            alt="Timeline separator"
            style={{ height: '55px' }}
          />
        </Stack>
      )}
    </>
  )
}

const desktopTimelineItem = (timelineItems: TimelineItem[], index: number) => {
  return (
    <>
      {index < timelineItems.length - 1 && (
        <Stack
          position={'absolute'}
          w={'100%'}
          top={'calc(50% - 5px)'}
          height={'10px'}
          left={'57px'}
          zIndex={1}
          justifyContent={'center'}
        >
          <img
            src={timelineSeparatorRight}
            alt="Timeline separator"
            style={{ width: 'calc(100% - 40px )' }}
          />
        </Stack>
      )}
    </>
  )
}

export default StaticTimeline
